import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Bot, 
  Send, 
  Sparkles, 
  ShoppingBag, 
  ArrowLeft, 
  Mic, 
  MicOff, 
  Volume2, 
  VolumeX, 
  ShieldCheck, 
  Package, 
  Truck, 
  Plus, 
  Check, 
  ExternalLink, 
  KeyRound, 
  Loader2, 
  PhoneCall, 
  Info,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Sparkle,
  Copy,
  UserCheck,
  User,
  MapPin,
  Phone,
  CreditCard,
  CheckCircle2,
  BadgeCheck,
  Star,
  Clock,
  RotateCcw,
  ThumbsUp,
  X,
  MessageSquare,
  AlertTriangle,
  HeartPulse,
  Smile,
  Home,
  Coffee,
  Sprout,
  Search,
  FileText
} from 'lucide-react';
import { Product } from '../types';
import { PinVerificationModal } from './PinVerificationModal';
import { db, doc, secureGetDoc } from '../firebase';

// 6 Product Categories exactly matching user image
const CHAT_CATEGORIES = [
  {
    id: 'health',
    name: 'สุขภาพ & อาหารเสริม',
    icon: HeartPulse,
    iconColor: 'text-[#E60067]',
    query: 'สุขภาพ & อาหารเสริม'
  },
  {
    id: 'beauty',
    name: 'ดูแลผิวหน้า & ผิวกาย',
    icon: Smile,
    iconColor: 'text-slate-700',
    query: 'ดูแลผิวหน้า & ผิวกาย'
  },
  {
    id: 'cosmetics',
    name: 'เครื่องสำอาง',
    icon: Sparkle,
    iconColor: 'text-slate-700',
    query: 'เครื่องสำอาง'
  },
  {
    id: 'home',
    name: 'ของใช้ในบ้าน & ซักผ้า',
    icon: Home,
    iconColor: 'text-slate-700',
    query: 'ของใช้ในบ้าน & ซักผ้า'
  },
  {
    id: 'food',
    name: 'กาแฟ & เครื่องดื่มสุขภาพ',
    icon: Coffee,
    iconColor: 'text-slate-700',
    query: 'กาแฟ & เครื่องดื่มสุขภาพ'
  },
  {
    id: 'agriculture',
    name: 'ปุ๋ย & นวัตกรรมเกษตร',
    icon: Sprout,
    iconColor: 'text-slate-700',
    query: 'ปุ๋ย & นวัตกรรมเกษตร'
  }
];

// Helper to generate 10 unique benefits based on product category/name
const generate10Benefits = (product: any): string[] => {
  const name = (product.name || '').toLowerCase();
  const cat = (product.category || '').toLowerCase();
  const desc = (product.description || product.highlight || '').toLowerCase();
  
  if (cat.includes('beauty') || cat.includes('ผิว') || name.includes('ครีม') || name.includes('เซรั่ม') || name.includes('บำรุง') || name.includes('ไฮยา') || name.includes('สบู่') || name.includes('โลชั่น')) {
    return [
      `ฟื้นฟูบำรุงลึกถึงชั้นเซลล์ผิวอย่างล้ำลึก เพื่อผิวกระจ่างใส สว่างมีออร่า และดูอมชมพูสุขภาพดี`,
      `เติมเต็มความชุ่มชื้นยาวนาน 24 ชั่วโมง ให้ผิวหน้าอิ่มน้ำ ฉ่ำวาว อิ่มฟู นุ่มเด้งราวกระจก`,
      `ลดเลือนริ้วรอยแห่งวัย รอยตีนกา และจุดด่างดำ รอยแดงจากสิวให้จางลงอย่างอ่อนโยน`,
      `อุดมด้วยสารต้านอนุมูลอิสระเข้มข้นพิเศษ ช่วยป้องกันผิวจากแสงแดด มลภาวะ และฝุ่น PM2.5`,
      `ปรับสภาพสีผิวให้สม่ำเสมอ เรียบเนียน นุ่มนวลน่าสัมผัส ทั่วใบหน้า ลำคอ และผิวกาย`,
      `สูตรบางเบาพิเศษ ซึมซาบเข้าสู่ผิวรวดเร็วภายใน 3 วินาที ไม่ทิ้งคราบขาวเหนียวเหนอะหนะ`,
      `กระตุ้นการสร้างคอลลาเจนและอีลาสตินใต้ผิวตามธรรมชาติ คืนความยืดหยุ่นให้ผิวกระชับเต่งตึง`,
      `ผ่านการทดสอบโดยแพทย์ผิวหนัง อ่อนโยน ปลอดภัย ปราศจากสารเคมีอันตรายและแอลกอริทึมสะสม`,
      `เป็นผลิตภัณฑ์ของแท้ 100% ส่งตรงจากคลังสินค้าหลักศูนย์กิฟฟารีน มั่นใจในคุณภาพสูงสุด`,
      `คุ้มค่าคุ้มราคาด้วยสิทธิพิเศษราคาสมาชิก พร้อมรับคะแนนสะสม PV สูงสุดถึง ${product.pv || Math.round(Number(product.memberPrice || product.price || 0) * 0.75)} PV ทันที`
    ];
  } else if (cat.includes('health') || cat.includes('อาหารเสริม') || name.includes('วิตามิน') || name.includes('น้ำมัน') || name.includes('แคปซูล') || name.includes('อาหาร') || name.includes('สมุนไพร') || name.includes('คอลลาเจน')) {
    return [
      `เสริมสร้างเกราะคุ้มกันร่างกายให้แข็งแรงจากภายใน ช่วยลดการอักเสบและการเจ็บป่วยเฉียบพลัน`,
      `ฟื้นบำรุงและดูแลระบบอวัยวะภายใน เซลล์สมอง หลอดเลือด และหัวใจให้แข็งแรงมีชีวิตชีวา`,
      `อุดมด้วยวิตามินและสารอาหารจำเป็นเข้มข้น คัดสรรเกรดพรีเมียมนำเข้าจากต่างประเทศ`,
      `ช่วยชะลอความเสื่อมของเซลล์ ต้านอนุมูลอิสระ คืนความอ่อนเยาว์และพลังงานบวกให้ร่างกาย`,
      `เพิ่มประสิทธิภาพในการนอนหลับ บรรเทาความเครียดล้าจากการทำงานหนัก ให้สดชื่นยามเช้า`,
      `ปลอดภัยสูงสุด ไร้สารตกค้าง ผ่านการรับรองความถูกต้องปลอดภัยจาก อย. และมาตรฐานสากล GMP`,
      `ปรับสมดุลระบบเผาผลาญ ระบบขับถ่าย และระบบไหลเวียนโลหิตให้ทำงานได้อย่างลงตัว`,
      `เหมาะอย่างยิ่งสำหรับผู้ที่ต้องการดูแลสุขภาพเชิงป้องกันและเพิ่มความแข็งแกร่งให้ร่างกาย`,
      `รับประกันคุณภาพพรีเมียม 100% ล็อตผลิตใหม่ล่าสุด ส่งตรงรวดเร็วจากกิฟฟารีนสำนักงานใหญ่`,
      `คุ้มค่าด้วยราคาสมาชิกประหยัด พร้อมรับคะแนน PV สะสมสูงสุดถึง ${product.pv || Math.round(Number(product.memberPrice || product.price || 0) * 0.75)} PV คืนกำไรสูงสุด`
    ];
  } else {
    return [
      `คิดค้นและพัฒนาด้วยเทคโนโลยีการผลิตขั้นสูง มาตรฐานสากลระดับโลก สะอาด ปลอดภัย 100%`,
      `คัดสรรส่วนผสมหลักและวัตถุดิบคุณภาพดีที่สุดเกรดนำเข้าเพื่อประสิทธิภาพสูงสุดในการใช้งาน`,
      `ผ่านการรับรองความปลอดภัยและขึ้นทะเบียนถูกต้องตามกฎหมายจาก อย. และมาตรฐานสูงสุด`,
      `ดีไซน์และเนื้อสัมผัสใช้งานง่าย สะดวก รวดเร็ว ตอบโจทย์ไลฟ์สไตล์การใช้ชีวิตในปัจจุบัน`,
      `อ่อนโยนต่อทุกคนในครอบครัว ปราศจากสารตกค้างสะสม และระคายเคืองต่อสิ่งแวดล้อม`,
      `บรรจุภัณฑ์ปิดแน่นหนา แข็งแรงทนทาน ออกแบบให้เก็บรักษาคุณค่าของสารบำรุงได้อย่างสมบูรณ์`,
      `เป็นผลิตภัณฑ์ยอดนิยมอันดับ 1 ในใจผู้ใช้จริง มีสถิติซื้อซ้ำอย่างต่อเนื่องและรีวิวความพึงพอใจสูง`,
      `คุ้มราคาประหยัด ให้ประสิทธิภาพคุ้มค่ากว่าทางเลือกทั่วไปในท้องตลาดหลายเท่าตัว`,
      `เป็นของแท้ 100% ส่งตรงจากศูนย์กิฟฟารีนผ่านระบบคลังสินค้าอุณหภูมิควบคุมที่ได้มาตรฐาน`,
      `คุ้มค่าสูงสุดสำหรับสมาชิกด้วยส่วนลดพิเศษ พร้อมคะแนนสะสม PV สูงสุดถึง ${product.pv || Math.round(Number(product.memberPrice || product.price || 0) * 0.75)} PV`
    ];
  }
};

interface ChatUIButton {
  id: string;
  label: string;
  action: 'confirm_order' | 'continue_shopping' | 'add_address' | 'confirm_address' | 'edit_address' | 'submit_order_native' | 'resume_chat' | 'rate_service' | string;
  style?: 'primary' | 'secondary' | 'outline' | 'success';
  payload?: any;
}

interface ChatMessage {
  id: string;
  sender: 'user' | 'pinkgiff';
  text: string;
  timestamp: string;
  suggestedProducts?: any[];
  orderStatusResult?: any;
  suggestedButtons?: ChatUIButton[];
  parsedAddress?: any;
  pinModalTrigger?: {
    actionType: string;
    orderSummary?: any;
  };
  isVoice?: boolean;
  messageType?: 'text' | 'product-benefits' | 'product-card' | 'product-details-unified';
  productDetail?: any;
}

interface ChatAndShopViewProps {
  onBackToStore: () => void;
  onAddToCart: (product: Product, quantity?: number) => void;
  onOpenCart: () => void;
  cartCount: number;
  customerUser?: any;
  cartItems?: any[];
  cartTotal?: number;
  onOpenProfile?: () => void;
  onOpenCheckout?: () => void;
  onProceedToCheckout?: () => void;
  onOpenAuth?: () => void;
}

interface SuggestedProductsSectionProps {
  products: any[];
  addedItemIds: Record<string, boolean>;
  onAddToCart: (product: any) => void;
  onViewDetails: (product: any) => void;
  onSearch: (query: string) => void;
}

const SuggestedProductsSection: React.FC<SuggestedProductsSectionProps> = ({
  products,
  addedItemIds,
  onAddToCart,
  onViewDetails,
  onSearch
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchTerm.trim();
    if (trimmed) {
      onSearch(trimmed);
      setSearchTerm('');
    }
  };

  return (
    <div className="space-y-2.5 pt-1">
      {/* Header with "สินค้าที่เกี่ยวข้อง:" and Search Input + Button beside it */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="text-[11px] sm:text-xs font-bold text-slate-700 flex items-center gap-1.5 shrink-0">
          <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#E60067]" />
          <span>สินค้าที่เกี่ยวข้อง:</span>
        </div>

        {/* ปุ่มค้นหาสินค้าข้าง ๆ ให้ลูกค้าพิมพ์ค้นหาได้ทั้งชื่อสินค้าและรหัสสินค้า */}
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-1.5 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-56 md:w-64">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="ค้นหาชื่อ หรือ รหัสสินค้า..."
              className="w-full pl-8 pr-7 py-1.5 rounded-xl text-xs bg-white border border-pink-200 focus:outline-none focus:border-[#E60067] focus:ring-1 focus:ring-[#E60067] placeholder:text-slate-400 text-slate-800 shadow-2xs transition-all"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
                title="ล้างข้อความ"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          <button
            type="submit"
            disabled={!searchTerm.trim()}
            className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#E60067] to-[#D80056] text-white text-xs font-bold shadow-xs hover:opacity-95 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shrink-0 cursor-pointer transition-all"
            title="ค้นหาสินค้า (พิมพ์ได้ทั้งชื่อสินค้า และ รหัสสินค้า)"
          >
            <Search className="w-3.5 h-3.5" />
            <span>ค้นหาสินค้า</span>
          </button>
        </form>
      </div>

      {/* Rich Suggested Products Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {products.map((p: any) => {
          const isAdded = addedItemIds[p.code];
          return (
            <div
              key={p.code}
              className="bg-white p-3 rounded-2xl border border-pink-100 shadow-xs hover:border-pink-300 transition-all flex flex-col justify-between gap-2.5 group"
            >
              <div className="flex gap-3">
                <img
                  src={p.image || 'https://www.giffarine.com/image/goods/82023-N1_01.jpg'}
                  alt={p.name}
                  className="w-16 h-16 rounded-xl object-cover bg-slate-50 shrink-0 border border-slate-100"
                />
                <div className="flex-1 min-w-0">
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-pink-50 text-[#E60067]">
                    รหัส {p.code}
                  </span>
                  <h4 className="font-bold text-xs text-slate-900 truncate mt-1">
                    {p.name}
                  </h4>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="font-black text-sm text-[#E60067]">
                      ฿{(p.memberPrice || p.price || 0).toLocaleString()}
                    </span>
                    {p.price && p.price > (p.memberPrice || 0) && (
                      <span className="text-[10px] text-slate-400 line-through">
                        ฿{p.price.toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-emerald-600 font-bold">
                    +{p.pv || Math.round((p.memberPrice || p.price || 0) * 0.75)} PV
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-auto pt-1">
                {/* 1. ปุ่มรายละเอียดสินค้า */}
                <button
                  type="button"
                  onClick={() => onViewDetails(p)}
                  className="w-full py-2 px-1.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 bg-white hover:bg-pink-50 text-slate-700 hover:text-[#E60067] border border-pink-200 hover:border-pink-300 shadow-2xs cursor-pointer active:scale-95"
                  title="ดูรายละเอียดและสรรพคุณสินค้า"
                >
                  <FileText className="w-3.5 h-3.5 text-[#E60067] shrink-0" />
                  <span className="truncate">รายละเอียดสินค้า</span>
                </button>

                {/* 2. ปุ่มสั่งซื้อลงตะกร้าทันที */}
                <button
                  type="button"
                  onClick={() => onAddToCart(p)}
                  className={`w-full py-2 px-1.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 cursor-pointer active:scale-95 shadow-2xs ${
                    isAdded
                      ? 'bg-emerald-600 text-white border border-emerald-600'
                      : 'bg-gradient-to-r from-[#E60067] to-[#D80056] text-white hover:opacity-95 shadow-pink-500/20'
                  }`}
                  title="สั่งซื้อลงตะกร้าทันที"
                >
                  {isAdded ? (
                    <>
                      <Check className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">เพิ่มลงตะกร้าแล้ว</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">สั่งซื้อลงตะกร้าทันที</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ChatAndShopView: React.FC<ChatAndShopViewProps> = ({
  onBackToStore,
  onAddToCart,
  onOpenCart,
  cartCount,
  customerUser,
  cartItems = [],
  cartTotal = 0,
  onOpenProfile,
  onOpenCheckout,
  onProceedToCheckout,
  onOpenAuth
}) => {
  // Calculate effective cart item count (robust against NaN, supports cartCount and cartItems array)
  const effectiveCartCount = useMemo(() => {
    if (typeof cartCount === 'number' && !isNaN(cartCount) && cartCount > 0) {
      return cartCount;
    }
    if (Array.isArray(cartItems) && cartItems.length > 0) {
      return cartItems.reduce((sum, item) => sum + (Number(item?.quantity) || 1), 0);
    }
    return 0;
  }, [cartCount, cartItems]);

  // Ticket Identifier for Tracking & Auditing
  const [ticketId, setTicketId] = useState<string>(() => {
    const d = new Date();
    const dateStr = d.toISOString().slice(0, 10).replace(/-/g, '');
    const rand = Math.floor(1000 + Math.random() * 9000);
    return `TKT-${dateStr}-${rand}`;
  });

  // 3-Minute Inactivity Timer State (180 Seconds)
  const INACTIVITY_TIMEOUT_SECONDS = 180;
  const [remainingSeconds, setRemainingSeconds] = useState<number>(INACTIVITY_TIMEOUT_SECONDS);
  const [isSessionClosed, setIsSessionClosed] = useState<boolean>(false);

  // CSAT Rating & Feedback State (1-5 Stars)
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(false);
  const [feedbackRating, setFeedbackRating] = useState<number>(5);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [feedbackText, setFeedbackText] = useState<string>('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean>(false);
  const [submittingFeedback, setSubmittingFeedback] = useState<boolean>(false);

  const [memberProfile, setMemberProfile] = useState<{
    customerId: string;
    fullName: string;
    phoneNumber: string;
    shippingAddress: string;
    isLoggedIn: boolean;
    role: string;
  }>({
    customerId: customerUser?.customerId || customerUser?.memberId || '',
    fullName: customerUser?.fullName || customerUser?.name || '',
    phoneNumber: customerUser?.phoneNumber || customerUser?.phone || '',
    shippingAddress: customerUser?.shippingAddress || '',
    isLoggedIn: Boolean(customerUser && (customerUser.id || customerUser.customerId || customerUser.email)),
    role: customerUser?.role || 'customer'
  });

  const initialIsMember = Boolean(customerUser && (customerUser.id || customerUser.customerId || customerUser.email));
  const initialName = customerUser?.fullName || customerUser?.name || '';
  
  const buildGreetingMessage = (isMember: boolean, name?: string): string => {
    const cleanName = (name || '').trim();
    if (isMember && cleanName && cleanName !== 'คุณลูกค้า' && cleanName !== 'CUSTOMER') {
      return `พิงค์กิฟ อินเทลลิเจนท์ สวัสดีคุณลูกค้าค่ะ   **Chat & Shop**  ยินดีให้บริการค่ะ\nวันนี้ คุณ ${cleanName} มีอะไรให้พิงค์ดูแลค่ะ`;
    }
    return `พิงค์กิฟ อินเทลลิเจนท์ สวัสดีคุณลูกค้าค่ะ   **Chat & Shop**  ยินดีให้บริการค่ะ\nวันนี้ คุณลูกค้ามีอะไรให้พิงค์ดูแลค่ะ`;
  };

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'init-1',
      sender: 'pinkgiff',
      text: buildGreetingMessage(initialIsMember, initialName),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);

  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [addedItemIds, setAddedItemIds] = useState<{ [key: string]: boolean }>({});
  
  // Voice Live Consultation State
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Zero-Trust Security PIN Modal State
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pinActionSummary, setPinActionSummary] = useState<any>(null);
  const [pinSuccessMessage, setPinSuccessMessage] = useState('');
  const [copiedTracking, setCopiedTracking] = useState(false);

  const messagesTopRef = useRef<HTMLDivElement>(null);
  const messagesBottomRef = useRef<HTMLDivElement>(null);
  const innerChatScrollRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    messagesTopRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollInnerChat = (direction: 'up' | 'down') => {
    if (innerChatScrollRef.current) {
      const scrollAmount = direction === 'up' ? -160 : 160;
      innerChatScrollRef.current.scrollBy({ top: scrollAmount, behavior: 'smooth' });
    }
  };

  const scrollInnerChatToBottom = () => {
    if (innerChatScrollRef.current) {
      innerChatScrollRef.current.scrollTo({
        top: innerChatScrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    scrollToTop();
    if (innerChatScrollRef.current) {
      innerChatScrollRef.current.scrollTo({
        top: innerChatScrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, loading]);

  // Reset Inactivity Timer back to 3 minutes on any user interaction
  const resetInactivityTimer = () => {
    if (!isSessionClosed) {
      setRemainingSeconds(INACTIVITY_TIMEOUT_SECONDS);
    }
  };

  // 3-Minute Inactivity Timer Countdown
  useEffect(() => {
    if (isSessionClosed || isFeedbackModalOpen) return;

    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleInactivityTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isSessionClosed, isFeedbackModalOpen]);

  // Triggered when 3-minute timer reaches 0
  const handleInactivityTimeout = () => {
    setIsSessionClosed(true);
    setIsFeedbackModalOpen(true);

    // Save ticket with timeout status
    syncTicketToBackend('timeout', undefined, 'เซสชันจบการทำงานอัตโนมัติเนื่องจากไม่มีการตอบกลับภายใน 3 นาที');
  };

  // Sync Ticket Record to Backend
  const syncTicketToBackend = async (
    status: 'active' | 'completed' | 'timeout' = 'active',
    ratingVal?: number,
    feedbackNote?: string
  ) => {
    try {
      await fetch('/api/chatandshop/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketId,
          customerName: memberProfile.fullName || customerUser?.fullName || 'คุณลูกค้า',
          memberId: memberProfile.customerId || customerUser?.customerId || '-',
          phone: memberProfile.phoneNumber || customerUser?.phoneNumber || '-',
          messages: messages.map(m => ({
            id: m.id,
            sender: m.sender,
            text: m.text,
            timestamp: m.timestamp
          })),
          messagesCount: messages.length,
          cartTotal: cartTotal || 0,
          status,
          rating: typeof ratingVal === 'number' ? ratingVal : (feedbackRating || 5),
          feedback: feedbackNote !== undefined ? feedbackNote : feedbackText
        })
      });
    } catch (err) {
      console.warn('Could not sync ticket:', err);
    }
  };

  // Submit Feedback Rating (1-5 Stars)
  const handleSubmitFeedback = async (explicitRating?: number) => {
    try {
      setSubmittingFeedback(true);
      const chosenRating = explicitRating || feedbackRating || 5;

      await syncTicketToBackend('completed', chosenRating, feedbackText);
      setFeedbackSubmitted(true);
    } catch (err) {
      console.error('Submit feedback error:', err);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  // Start New Chat Session
  const handleStartNewChat = () => {
    const d = new Date();
    const dateStr = d.toISOString().slice(0, 10).replace(/-/g, '');
    const rand = Math.floor(1000 + Math.random() * 9000);
    const newId = `TKT-${dateStr}-${rand}`;

    setTicketId(newId);
    setRemainingSeconds(INACTIVITY_TIMEOUT_SECONDS);
    setIsSessionClosed(false);
    setIsFeedbackModalOpen(false);
    setFeedbackSubmitted(false);
    setFeedbackRating(5);
    setFeedbackText('');
    const isMember = Boolean(memberProfile.isLoggedIn || memberProfile.customerId);
    const greetingText = buildGreetingMessage(isMember, memberProfile.fullName);

    setMessages([
      {
        id: 'init-new-' + Date.now(),
        sender: 'pinkgiff',
        text: greetingText,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        suggestedButtons: (isMember && cartItems.length > 0) ? [
          { id: 'btn-summary', label: '🛒 สรุปยอดและตรวจสอบที่อยู่จัดส่ง', action: 'confirm_order', style: 'primary' }
        ] : undefined
      }
    ]);
  };

  // Synchronize customer profile & Giffarine ID from Firestore or cache
  useEffect(() => {
    let isMounted = true;

    const syncCustomerProfile = async () => {
      let customerId = customerUser?.customerId || customerUser?.memberId || '';
      let fullName = customerUser?.fullName || customerUser?.name || '';
      let phoneNumber = customerUser?.phoneNumber || customerUser?.phone || '';
      let shippingAddress = customerUser?.shippingAddress || '';
      const isLoggedIn = Boolean(customerUser && (customerUser.id || customerUser.customerId || customerUser.email));
      const role = customerUser?.role || 'customer';

      // 1. If authenticated, fetch from Firestore private user profile
      if (customerUser?.id) {
        try {
          const profilePath = `users/${customerUser.id}/private/profile`;
          const profileDocRef = doc(db, 'users', customerUser.id, 'private', 'profile');
          const profileSnap: any = await secureGetDoc(profileDocRef, profilePath);
          if (profileSnap && typeof profileSnap.exists === 'function' && profileSnap.exists()) {
            const data: any = profileSnap.data();
            if (data?.customerId && !customerId) customerId = data.customerId;
            if (data?.fullName && !fullName) fullName = data.fullName;
            if (data?.phoneNumber && !phoneNumber) phoneNumber = data.phoneNumber;
            if (data?.shippingAddress && !shippingAddress) shippingAddress = data.shippingAddress;
          }
        } catch (e) {
          console.warn('Could not read Firestore private profile:', e);
        }
      }

      // 2. Fallback to cached profile if any field is still empty
      if (!shippingAddress || !customerId) {
        try {
          const cached = localStorage.getItem('giffarine_member_profile');
          if (cached) {
            const parsed = JSON.parse(cached);
            if (!customerId && parsed.customerId) customerId = parsed.customerId;
            if (!fullName && parsed.fullName) fullName = parsed.fullName;
            if (!phoneNumber && parsed.phoneNumber) phoneNumber = parsed.phoneNumber;
            if (!shippingAddress && parsed.shippingAddress) shippingAddress = parsed.shippingAddress;
          }
        } catch (_) {}
      }

      if (isMounted) {
        setMemberProfile({
          customerId,
          fullName,
          phoneNumber,
          shippingAddress,
          isLoggedIn,
          role
        });

        // Personalize initial greeting based on member status and name
        const isMember = Boolean(isLoggedIn || customerId);
        const greetingText = buildGreetingMessage(isMember, fullName);

        setMessages(prev => {
          if (prev.length === 1 && (prev[0].id === 'init-1' || prev[0].id === 'init-member-1')) {
            return [
              {
                id: isMember ? 'init-member-1' : 'init-1',
                sender: 'pinkgiff',
                text: greetingText,
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                suggestedButtons: (isMember && cartItems.length > 0) ? [
                  { id: 'btn-summary', label: '🛒 สรุปยอดและตรวจสอบที่อยู่จัดส่ง', action: 'confirm_order', style: 'primary' }
                ] : undefined
              }
            ];
          }
          return prev;
        });
      }
    };

    syncCustomerProfile();

    return () => {
      isMounted = false;
    };
  }, [customerUser]);

  const handleSendMessage = async (textToSend?: string) => {
    const text = (textToSend || inputValue).trim();
    if (!text || loading) return;

    resetInactivityTimer();

    const userMsgId = 'user-' + Date.now();
    const newMsg: ChatMessage = {
      id: userMsgId,
      sender: 'user',
      text: text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, newMsg]);
    setInputValue('');
    setLoading(true);

    try {
      // Build history for context
      const historyPayload = messages.slice(-8).map(m => ({
        role: m.sender === 'user' ? 'user' : 'model',
        content: m.text
      }));

      const res = await fetch('/api/chatandshop/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: historyPayload,
          customerContext: {
            memberId: memberProfile.customerId || customerUser?.customerId || customerUser?.memberId || '-',
            name: memberProfile.fullName || customerUser?.fullName || customerUser?.name || 'คุณลูกค้า',
            phone: memberProfile.phoneNumber || customerUser?.phoneNumber || customerUser?.phone || '-',
            address: memberProfile.shippingAddress || customerUser?.shippingAddress || '',
            isMember: Boolean(memberProfile.customerId || customerUser?.customerId),
            role: memberProfile.role || customerUser?.role || 'customer',
            cartSummary: cartItems.length > 0
              ? cartItems.map((i: any) => {
                  const p = i.product || i;
                  const qty = Number(i.quantity) || 1;
                  const price = Number(p.memberPrice || p.price || 0);
                  return `${p.name} x${qty} (฿${(price * qty).toLocaleString()})`;
                }).join(', ')
              : 'ไม่มีสินค้าในตะกร้า',
            cart: cartItems.map((i: any) => {
              const p = i.product || i;
              const qty = Number(i.quantity) || 1;
              const price = Number(p.memberPrice || p.price || 0);
              return {
                id: p.id || p.code,
                name: p.name,
                price: price,
                quantity: qty
              };
            }),
            cartTotal: cartTotal || 0
          }
        })
      });

      const json = await res.json();

      if (json.success && json.data) {
        const botReply: ChatMessage = {
          id: 'bot-' + Date.now(),
          sender: 'pinkgiff',
          text: json.data.reply,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          suggestedProducts: json.data.suggestedProducts,
          orderStatusResult: json.data.orderStatusResult,
          suggestedButtons: json.data.suggestedButtons,
          parsedAddress: json.data.parsedAddress,
          pinModalTrigger: json.data.pinModalTrigger
        };

        setMessages(prev => [...prev, botReply]);

        // Auto sync ticket with latest messages
        setTimeout(() => {
          syncTicketToBackend('active');
        }, 500);

        // If address was parsed or updated by AI, reflect it dynamically in the member profile view
        if (json.data.parsedAddress) {
          const parsed = json.data.parsedAddress;
          const fullParsedAddr = `${parsed.houseNo || ''} ${parsed.village || ''} ${parsed.lane || ''} ${parsed.road || ''} ${parsed.subdistrict || ''} ${parsed.district || ''} ${parsed.province || ''} ${parsed.postalCode || ''}`.replace(/\s+/g, ' ').trim();
          if (fullParsedAddr) {
            setMemberProfile(prev => ({
              ...prev,
              shippingAddress: fullParsedAddr,
              fullName: parsed.name || prev.fullName,
              phoneNumber: parsed.phone || prev.phoneNumber
            }));
          }
        }

        // Auto trigger PIN modal if Zero-Trust authorization requested
        if (json.data.pinModalTrigger) {
          setPinActionSummary(json.data.pinModalTrigger.orderSummary);
          setTimeout(() => {
            setIsPinModalOpen(true);
          }, 800);
        }
      } else {
        throw new Error(json.error || 'ไม่สามารถติดต่อเซิร์ฟเวอร์ได้');
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [
        ...prev,
        {
          id: 'bot-err-' + Date.now(),
          sender: 'pinkgiff',
          text: 'ขออภัยด้วยนะคะ เกิดปัญหาการเชื่อมต่อชั่วคราว คุณลูกค้าสามารถลองใหม่อีกครั้ง หรือสั่งซื้อผ่านหน้าร้านปกติได้เลยนะคะ 💖',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleShowProductDetails = (product: any) => {
    resetInactivityTimer();
    
    // Add user message to conversation
    const userMsg: ChatMessage = {
      id: 'user-detail-' + Date.now(),
      sender: 'user',
      text: `ขอทราบรายละเอียดสินค้า: ${product.name} (รหัส ${product.code || product.id})`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    setTimeout(() => {
      // Single unified Bubble: contains both Product Card and 10 benefits in 1 bubble!
      const unifiedMsg: ChatMessage = {
        id: 'bot-detail-unified-' + Date.now(),
        sender: 'pinkgiff',
        text: `รายละเอียดสินค้าและคุณประโยชน์เด่น: ${product.name}`,
        messageType: 'product-details-unified',
        productDetail: product,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, unifiedMsg]);
      setLoading(false);
      setTimeout(() => {
        syncTicketToBackend('active');
      }, 500);
    }, 400);
  };

  const handleAddProductToCart = (p: any) => {
    resetInactivityTimer();

    const formattedProduct: Product = {
      id: p.id || `giff-${p.code}`,
      code: p.code,
      name: p.name,
      category: p.category || 'health',
      price: p.price,
      memberPrice: p.memberPrice || Math.round(p.price * 0.75),
      pv: p.pv || Math.round(p.price * 0.75),
      unit: p.unit || 'ชิ้น',
      image: p.image || 'https://www.giffarine.com/image/goods/82023-N1_01.jpg',
      badge: p.badge || 'แนะนำ',
      description: p.description || p.name,
      highlight: p.highlight || `รหัส ${p.code} • ${p.pv || 0} PV`
    };

    onAddToCart(formattedProduct, 1);
    setAddedItemIds(prev => ({ ...prev, [p.code]: true }));

    setTimeout(() => {
      setAddedItemIds(prev => ({ ...prev, [p.code]: false }));
    }, 2000);

    setTimeout(() => {
      setMessages(prev => [
        ...prev,
        {
          id: 'bot-cart-followup-' + Date.now(),
          sender: 'pinkgiff',
          text: `พิงค์กิฟเพิ่ม **${p.name}** ลงตะกร้าให้แล้วน้า 😊 มีรับสินค้าตัวไหนเพิ่มอีกไหมคะ?`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          suggestedButtons: [
            { id: 'btn-more-' + Date.now(), label: '🛍️ เลือกดูสินค้าเพิ่ม', action: 'continue_shopping', style: 'outline' },
            { id: 'btn-done-' + Date.now(), label: '🛒 พอแล้ว สรุปยอดเลย', action: 'confirm_order', style: 'primary' }
          ]
        }
      ]);
    }, 400);
  };

  const handleButtonClick = (btn: ChatUIButton) => {
    resetInactivityTimer();

    if (!btn) return;

    switch (btn.action) {
      case 'add_to_cart':
        if (btn.payload) {
          handleAddProductToCart(btn.payload);
        }
        break;
      case 'confirm_order':
        if ((cartItems?.length || 0) === 0) {
          handleSendMessage('พอแล้ว สรุปยอดเลย');
          break;
        }
        if ((btn.label || '').includes('ยืนยันคำสั่งซื้อ') || (btn.label || '').includes('ชำระเงิน')) {
          if (onProceedToCheckout) {
            onProceedToCheckout();
          } else if (onOpenCheckout) {
            onOpenCheckout();
          } else {
            onOpenCart();
          }
        } else {
          handleSendMessage('พอแล้ว สรุปยอดเลย');
        }
        break;
      case 'continue_shopping':
        handleSendMessage('อยากดูสินค้าเพิ่มค่ะ');
        break;
      case 'rate_service':
        setIsFeedbackModalOpen(true);
        break;
      case 'add_address':
        if (onOpenProfile) {
          onOpenProfile();
        } else {
          setMessages(prev => [
            ...prev,
            {
              id: 'bot-prompt-addr-' + Date.now(),
              sender: 'pinkgiff',
              text: 'คุณลูกค้าสามารถพิมพ์ **ชื่อ-เบอร์โทร-ที่อยู่จัดส่ง** (เช่น คุณสมชาย 0812345678 123/45 ซ.สุขุมวิท แขวงคลองเตย เขตคลองเตย กทม 10110) เข้ามาในช่องแชตนี้ได้เลยนะคะ พิงค์กิฟจะช่วยตรวจสอบให้อัตโนมัติเลยค่ะ 📍',
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            }
          ]);
        }
        break;
      case 'confirm_address':
        handleSendMessage('ยืนยันข้อมูล');
        break;
      case 'edit_address':
        setMessages(prev => [
          ...prev,
          {
            id: 'bot-edit-addr-' + Date.now(),
            sender: 'pinkgiff',
            text: 'รบกวนคุณลูกค้าพิมพ์ที่อยู่จัดส่งที่ถูกต้องใหม่อีกครั้งในช่องแชตได้เลยน้า เดี๋ยวพิงค์กิฟบันทึกให้อัตโนมัติค่ะ 📝',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }
        ]);
        break;
      case 'submit_order_native':
      case 'checkout':
      case 'confirm_order_checkout':
        if ((cartItems?.length || 0) === 0) {
          handleSendMessage('พอแล้ว สรุปยอดเลย');
          break;
        }
        if (onProceedToCheckout) {
          onProceedToCheckout();
        } else if (onOpenCheckout) {
          onOpenCheckout();
        } else {
          onOpenCart();
        }
        break;
      case 'resume_chat':
        handleSendMessage('ทำรายการต่อค่ะ');
        break;
      default:
        handleSendMessage(btn.label || '');
        break;
    }
  };

  const handlePinSuccess = () => {
    setIsPinModalOpen(false);
    setPinSuccessMessage('✅ ยืนยันรหัส PIN ปลอดภัยสำเร็จ! ระบบบันทึกคำสั่งซื้อของคุณลูกค้าเข้าสู่ระบบจัดส่งเดลิเวอรี่เรียบร้อยแล้วค่ะ');
    
    // Add confirmation message to chat
    setMessages(prev => [
      ...prev,
      {
        id: 'bot-pin-confirmed-' + Date.now(),
        sender: 'pinkgiff',
        text: '🎉 **ยืนยันรหัส PIN 6 หลักสำเร็จเรียบร้อยค่ะ!**\n\nระบบความปลอดภัย Zero-Trust ได้อนุมัติรายการของคุณลูกค้าเรียบร้อยแล้ว สินค้าเตรียมแพ็กและจัดส่งทันที คุณลูกค้าสามารถติดตามสถานะพัสดุได้ตลอด 24 ชม. เลยนะคะ ขอบคุณที่ไว้วางใจพิงค์กิฟค่ะ 🥰📦✨',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        suggestedButtons: [
          { id: 'btn-rate-' + Date.now(), label: '⭐ ประเมินความพึงพอใจการบริการ (1-5 ดาว)', action: 'rate_service', style: 'primary' }
        ]
      }
    ]);
  };

  const copyToClipboard = (txt: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(txt).catch(() => {});
      }
    } catch (e) {
      console.warn('Clipboard write error:', e);
    }
    setCopiedTracking(true);
    setTimeout(() => setCopiedTracking(false), 2000);
  };

  // Format MM:SS for Timer
  const formatTimer = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/40 via-white to-slate-50 flex flex-col font-['Prompt',sans-serif]">
      {/* Top Decoupled Navigation & Input Container */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-pink-100 shadow-2xs">
        {/* Navigation Header */}
        <header className="px-4 sm:px-6 py-2.5 flex items-center justify-between border-b border-pink-50">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToStore}
              className="p-2 -ml-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer flex items-center gap-1.5 text-xs font-bold"
              title="กลับสู่หน้าร้านหลัก"
            >
              <ArrowLeft className="w-4 h-4 text-[#E60067]" />
              <span className="hidden sm:inline">หน้าร้านหลัก</span>
            </button>

            <div className="flex items-center gap-2.5">
              <div className="relative">
                <div className="w-9 h-9 rounded-2xl bg-gradient-to-tr from-[#E60067] via-pink-500 to-rose-400 flex items-center justify-center text-white shadow-md shadow-pink-500/20">
                  <Bot className="w-4 h-4" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full"></span>
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <h1 className="font-extrabold text-sm sm:text-base text-slate-900 leading-tight">
                    PINKGIFF
                  </h1>
                  <span className="px-1.5 py-0.5 rounded-full bg-pink-100 text-[#E60067] text-[10px] font-black uppercase tracking-wider">
                    Chat & Shop
                  </span>
                  {/* Ticket Badge */}
                  <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 font-mono text-[10px] font-bold border border-slate-200">
                    #{ticketId}
                  </span>
                </div>
                {memberProfile.isLoggedIn && (
                  <p className="text-[11px] text-slate-500 flex items-center gap-1">
                    <Sparkle className="w-3 h-3 text-[#E60067]" />
                    <span>สนทนากับ: คุณ {memberProfile.fullName || 'สมาชิก'} (Giffarine ID: {memberProfile.customerId || 'VIP'})</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action Controls, Inactivity Timer & Rating Button */}
          <div className="flex items-center gap-2">
            {/* Inactivity 3-Minute Timer Pill */}
            <div 
              onClick={resetInactivityTimer}
              title="ระบบจะปิดเซสชันอัตโนมัติหากไม่มีการโต้ตอบภายใน 3 นาที (คลิกเพื่อรีเซ็ตเวลา)"
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-mono font-bold cursor-pointer transition-all border ${
                remainingSeconds <= 30
                  ? 'bg-rose-50 text-rose-700 border-rose-300 animate-pulse'
                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
              }`}
            >
              <Clock className={`w-3.5 h-3.5 ${remainingSeconds <= 30 ? 'text-rose-600' : 'text-slate-400'}`} />
              <span>{formatTimer(remainingSeconds)}</span>
              {remainingSeconds <= 30 && (
                <span className="text-[10px] font-sans text-rose-600 font-extrabold hidden sm:inline">
                  (ต่อเวลา)
                </span>
              )}
            </div>

            {/* End Chat / Rate Service Button */}
            <button
              onClick={() => setIsFeedbackModalOpen(true)}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-xs font-bold transition-all shadow-2xs"
              title="ประเมินความพึงพอใจการบริการ (1-5 ดาว)"
            >
              <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
              <span className="hidden sm:inline">ประเมินบริการ</span>
            </button>

            {/* LINE Official Account Shortcut */}
            <a
              href="https://lin.ee/PSM7qHqg"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#06C755] hover:bg-[#05b34c] text-white text-xs font-bold transition-all shadow-xs"
              title="แชตและสั่งซื้อผ่าน LINE Official Account (@997wvsow)"
            >
              <span className="font-black text-[11px] tracking-tight">LINE</span>
              <span className="hidden xl:inline">@997wvsow</span>
            </a>

            {/* Voice Live Consultation Toggle */}
            <button
              onClick={() => setIsVoiceActive(!isVoiceActive)}
              className={`px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer border ${
                isVoiceActive 
                  ? 'bg-rose-500 text-white border-rose-600 shadow-md shadow-rose-500/25 animate-pulse' 
                  : 'bg-pink-50 text-[#E60067] border-pink-200 hover:bg-pink-100'
              }`}
            >
              {isVoiceActive ? <Mic className="w-3.5 h-3.5" /> : <PhoneCall className="w-3.5 h-3.5" />}
              <span className="hidden md:inline">{isVoiceActive ? 'สายสนทนาสดเปิดอยู่' : 'สนทนาเสียงสด'}</span>
            </button>

            {/* Cart Trigger */}
            <button
              id="chat-header-cart-btn"
              onClick={onOpenCart}
              className="relative p-2 text-slate-700 hover:text-[#E60067] bg-slate-50 hover:bg-pink-50 rounded-xl border border-slate-200 transition-colors cursor-pointer"
              title={`ตะกร้าสินค้า (${effectiveCartCount} ชิ้น)`}
            >
              <ShoppingBag className="w-5 h-5" />
              {effectiveCartCount > 0 && (
                <span
                  id="chat-header-cart-badge"
                  className="absolute -top-1.5 -right-1.5 bg-[#E60067] text-white text-[10px] font-black min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center shadow-xs border-2 border-white ring-1 ring-pink-500/30 animate-in zoom-in"
                >
                  {effectiveCartCount > 99 ? '99+' : effectiveCartCount}
                </span>
              )}
            </button>
          </div>
        </header>

        {/* Voice Mode Banner (If Active) */}
        {isVoiceActive && (
          <div className="bg-gradient-to-r from-rose-600 via-[#E60067] to-pink-600 text-white py-2 px-4 text-xs font-semibold flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
              <span>กำลังเชื่อมต่อสัญญาณเสียงแบบเรียลไทม์ (WebRTC / Gemini Live API)</span>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setIsMuted(!isMuted)} 
                className="p-1 hover:bg-white/20 rounded cursor-pointer"
                title={isMuted ? 'เปิดไมค์' : 'ปิดไมค์'}
              >
                {isMuted ? <MicOff className="w-4 h-4 text-rose-200" /> : <Mic className="w-4 h-4" />}
              </button>
              <button 
                onClick={() => setSoundEnabled(!soundEnabled)} 
                className="p-1 hover:bg-white/20 rounded cursor-pointer"
                title={soundEnabled ? 'ปิดเสียง' : 'เปิดเสียง'}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-rose-200" />}
              </button>
              <button 
                onClick={() => setIsVoiceActive(false)}
                className="px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-[11px] font-bold cursor-pointer"
              >
                วางสาย
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Conversation Stream */}
      <main className="flex-1 max-w-4xl lg:max-w-5xl xl:max-w-6xl w-full mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Ticket Header Banner */}
        <div className="p-3 bg-white rounded-2xl border border-slate-200/80 shadow-2xs flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-teal-600" />
            <span>Ticket: <strong className="text-slate-800 font-mono">#{ticketId}</strong></span>
            <span className="text-slate-300">|</span>
            <span>สถานะ: <span className="text-emerald-600 font-bold">กำลังสนทนาสด</span></span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span>ระบบตัดอัตโนมัติใน 3 นาที</span>
            <button
              onClick={() => setIsFeedbackModalOpen(true)}
              className="text-amber-700 font-bold hover:underline flex items-center gap-1"
            >
              <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
              <span>ให้คะแนน 1-5 ดาว</span>
            </button>
          </div>
        </div>

        {/* PIN Verification Success Notification */}
        {pinSuccessMessage && (
          <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 p-4 rounded-2xl flex items-start gap-3 shadow-xs animate-in fade-in">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-xs">
              <div className="font-bold">{pinSuccessMessage}</div>
              <div className="text-emerald-700 mt-1">ระบบ Zero-Trust บันทึกสิทธิประโยชน์และคะแนน PV ให้เรียบร้อยแล้วค่ะ</div>
            </div>
          </div>
        )}

        {/* Top Scroll Anchor */}
        <div ref={messagesTopRef} className="h-0.5 scroll-mt-32" />

        {/* Typing Loading Indicator - Placed at top where latest answer appears */}
        {loading && (
          <div className="flex items-start gap-2.5 sm:gap-3.5 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-2xl bg-gradient-to-tr from-[#E60067] via-pink-500 to-rose-400 text-white flex items-center justify-center shrink-0 mt-1 shadow-xs">
              <Bot className="w-4 h-4 sm:w-5 sm:h-5 animate-pulse" />
            </div>
            <div className="bg-white p-3.5 rounded-2xl rounded-tl-none border-2 border-pink-200 shadow-sm flex items-center gap-2 text-xs text-slate-600">
              <Loader2 className="w-4 h-4 text-[#E60067] animate-spin" />
              <span>รอซักครู่นะคะ (เนื่องจากสินค้ามีหลายรายการ กำลังประมวลผลค้นหาข้อมูลสินค้าค่ะ...)</span>
            </div>
          </div>
        )}

        {/* SINGLE MASTER CONVERSATION BUBBLE (+50% Larger, Internal Scroll Navigation, No Duplicate Outer Bubbles) */}
        {(() => {
          const latestBotMsg = [...messages].reverse().find(m => m.sender === 'pinkgiff') || messages[0];
          const hasSuggestedProducts = latestBotMsg?.suggestedProducts && latestBotMsg.suggestedProducts.length > 0;
          const hasOrderStatus = Boolean(latestBotMsg?.orderStatusResult);
          const isProductDetailUnified = latestBotMsg?.messageType === 'product-details-unified' && latestBotMsg?.productDetail;

          return (
            <div className="w-full flex items-start gap-3 sm:gap-4 md:gap-5 animate-in fade-in duration-300">
              {/* Bot Avatar (+50% Larger) */}
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-3xl bg-gradient-to-tr from-[#E60067] via-pink-500 to-rose-400 shadow-md shadow-pink-500/25 ring-4 ring-pink-100 flex items-center justify-center text-white shrink-0 mt-2">
                <Bot className="w-6 h-6 sm:w-8 sm:h-8 md:w-9 md:h-9" />
              </div>

              {/* Master Bubble Container (+50% Larger with generous padding & rounded corners) */}
              <div className="flex-1 min-w-0 bg-white text-slate-900 border-2 border-pink-200 rounded-[32px] sm:rounded-[36px] p-5 sm:p-7 md:p-8 lg:p-9 shadow-lg shadow-pink-500/10 ring-4 ring-pink-50/70 space-y-6">
                
                {/* 50 / 50 Split Layout: Left for Text & Internal Scroll, Right for Categories & Cart */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 lg:gap-8 items-stretch">
                  
                  {/* Left Half: Dialogue Answers & Internal Scrollable Viewport */}
                  <div className="flex flex-col justify-between space-y-3 lg:border-r lg:border-pink-100 lg:pr-6">
                    
                    {/* Top Control Bar with Internal Scroll Buttons */}
                    <div className="flex items-center justify-between gap-2 pb-2.5 border-b border-pink-100/80">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#E60067] animate-pulse" />
                        <span className="text-xs sm:text-sm font-extrabold text-slate-900">
                          พิงค์กิฟ อินเทลลิเจนท์ (AI)
                        </span>
                      </div>

                      {/* Internal Scroll Controls */}
                      <div className="flex items-center gap-1 bg-pink-50/80 p-1 rounded-xl border border-pink-100">
                        <button
                          type="button"
                          onClick={() => scrollInnerChat('up')}
                          className="p-1 sm:px-2 sm:py-1 rounded-lg hover:bg-white text-slate-600 hover:text-[#E60067] text-[11px] font-bold transition-all flex items-center gap-1 cursor-pointer shadow-2xs active:scale-95"
                          title="เลื่อนขึ้นเพื่อดูข้อความก่อนหน้า"
                        >
                          <ChevronUp className="w-4 h-4 text-[#E60067]" />
                          <span className="hidden sm:inline">เลื่อนขึ้น</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => scrollInnerChat('down')}
                          className="p-1 sm:px-2 sm:py-1 rounded-lg hover:bg-white text-slate-600 hover:text-[#E60067] text-[11px] font-bold transition-all flex items-center gap-1 cursor-pointer shadow-2xs active:scale-95"
                          title="เลื่อนลง"
                        >
                          <ChevronDown className="w-4 h-4 text-[#E60067]" />
                          <span className="hidden sm:inline">เลื่อนลง</span>
                        </button>
                        <button
                          type="button"
                          onClick={scrollInnerChatToBottom}
                          className="p-1 sm:px-2 sm:py-1 rounded-lg hover:bg-white text-[#E60067] text-[11px] font-extrabold transition-all cursor-pointer shadow-2xs active:scale-95"
                          title="ไปที่คำตอบล่าสุด"
                        >
                          ล่าสุด
                        </button>
                      </div>
                    </div>

                    {/* Scrollable Response Viewport */}
                    <div
                      ref={innerChatScrollRef}
                      className="min-h-[220px] sm:min-h-[260px] md:min-h-[300px] max-h-[380px] sm:max-h-[440px] md:max-h-[480px] overflow-y-auto pr-2 space-y-4 scroll-smooth scrollbar-thin scrollbar-thumb-pink-200"
                    >
                      {/* Render All Dialogue Entries within this single bubble */}
                      {messages.map((msg, mIdx) => {
                        const isUserMsg = msg.sender === 'user';
                        return (
                          <div
                            key={msg.id || mIdx}
                            className={`p-3 sm:p-4 rounded-2xl transition-all ${
                              isUserMsg
                                ? 'bg-pink-50/80 border border-pink-100 text-slate-800 ml-4'
                                : 'bg-slate-50/60 border border-slate-100 text-slate-900'
                            }`}
                          >
                            <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 mb-1.5 pb-1 border-b border-pink-100/50">
                              <span className={isUserMsg ? 'text-[#E60067]' : 'text-slate-700'}>
                                {isUserMsg ? '👤 คำถามของคุณ:' : '💖 คำตอบจากพิงค์กิฟ:'}
                              </span>
                              <span className="text-[10px] font-mono text-slate-400">{msg.timestamp}</span>
                            </div>
                            <div className="whitespace-pre-line text-sm sm:text-base md:text-[17px] leading-relaxed font-normal text-slate-800">
                              {msg.text}
                            </div>
                          </div>
                        );
                      })}

                      {/* Loading State Inside Response Viewport */}
                      {loading && (
                        <div className="p-4 rounded-2xl bg-gradient-to-r from-pink-50 via-rose-50 to-white border border-pink-200 flex items-center gap-3 animate-in fade-in">
                          <Loader2 className="w-5 h-5 text-[#E60067] animate-spin shrink-0" />
                          <div className="text-xs sm:text-sm text-slate-700 font-medium">
                            กำลังประมวลผลค้นหาข้อมูลและสินค้ากิฟฟารีนให้คุณลูกค้าค่ะ...
                          </div>
                        </div>
                      )}

                      {/* Interactive Action Buttons Inside Left Pane */}
                      {latestBotMsg?.suggestedButtons && latestBotMsg.suggestedButtons
                        .filter(btn => Boolean(btn && btn.style !== 'outline'))
                        .filter(btn => {
                          const label = btn?.label || '';
                          const action = btn?.action || '';
                          const id = btn?.id || '';
                          const isSummaryOrCheckout = 
                            id === 'btn-summary' || 
                            id === 'btn-finish-cart' ||
                            label.includes('สรุปยอด') || 
                            label.includes('สรุปรายการ') || 
                            label.includes('ตรวจสอบที่อยู่จัดส่ง') ||
                            label.includes('ยืนยันคำสั่งซื้อ') ||
                            action === 'confirm_order' ||
                            action === 'checkout' ||
                            action === 'submit_order_native' ||
                            action === 'confirm_order_checkout';
                          if (isSummaryOrCheckout && (cartItems?.length || 0) === 0) {
                            return false;
                          }
                          return true;
                        }).length > 0 && (
                        <div className="pt-2 flex flex-wrap gap-2">
                          {latestBotMsg.suggestedButtons
                            .filter(btn => Boolean(btn && btn.style !== 'outline'))
                            .filter(btn => {
                              const label = btn?.label || '';
                              const action = btn?.action || '';
                              const id = btn?.id || '';
                              const isSummaryOrCheckout = 
                                id === 'btn-summary' || 
                                id === 'btn-finish-cart' ||
                                label.includes('สรุปยอด') || 
                                label.includes('สรุปรายการ') || 
                                label.includes('ตรวจสอบที่อยู่จัดส่ง') ||
                                label.includes('ยืนยันคำสั่งซื้อ') ||
                                action === 'confirm_order' ||
                                action === 'checkout' ||
                                action === 'submit_order_native' ||
                                action === 'confirm_order_checkout';
                              if (isSummaryOrCheckout && (cartItems?.length || 0) === 0) {
                                return false;
                              }
                              return true;
                            })
                            .map((btn) => {
                              const label = btn?.label || '';
                              const isConfirmOrder = label.includes('ยืนยันคำสั่งซื้อ') || btn.action === 'submit_order_native' || btn.action === 'checkout' || btn.action === 'confirm_order_checkout';
                              const isPrimary = btn.style === 'primary' || isConfirmOrder;
                              const isSuccess = btn.style === 'success' || btn.action === 'confirm_address';
                              return (
                                <button
                                  key={btn.id || `btn-${Math.random()}`}
                                  type="button"
                                  onClick={() => handleButtonClick(btn)}
                                  className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all shadow-md cursor-pointer flex items-center gap-1.5 active:scale-95 ${
                                    isPrimary || isConfirmOrder
                                      ? 'bg-gradient-to-r from-[#E60067] to-[#D80056] text-white hover:opacity-95 shadow-pink-500/25 ring-2 ring-pink-400/30'
                                      : isSuccess
                                      ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-500/20'
                                      : 'bg-white hover:bg-pink-50 text-[#E60067] border border-pink-200 hover:border-pink-300'
                                  }`}
                                >
                                  {isConfirmOrder ? (
                                    <>
                                      <ShoppingBag className="w-4 h-4 shrink-0" />
                                      <Check className="w-4 h-4 shrink-0" />
                                    </>
                                  ) : (
                                    <>
                                      {isPrimary && <ShoppingBag className="w-3.5 h-3.5 shrink-0" />}
                                      {isSuccess && <Check className="w-3.5 h-3.5 shrink-0" />}
                                    </>
                                  )}
                                  {btn.action === 'rate_service' && <Star className="w-3.5 h-3.5 text-amber-300 fill-amber-300 shrink-0" />}
                                  <span>{label.replace(/^[🛒✅📝📍✏️\s]+/, '')}</span>
                                </button>
                              );
                            })}
                        </div>
                      )}
                    </div>

                    {/* Footer Info */}
                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] font-mono text-slate-400">
                      <span>รหัสเซสชัน #{ticketId}</span>
                      <span>{latestBotMsg?.timestamp}</span>
                    </div>
                  </div>

                  {/* Right Half: 2 Rows x 3 Columns Category Pearl Spheres (+50% Larger) + Shopping Cart Button */}
                  <div className="relative rounded-3xl bg-gradient-to-b from-white/95 via-rose-50/25 to-pink-50/35 p-4 sm:p-6 md:p-7 border border-pink-100 shadow-sm flex flex-col justify-between">
                    
                    {/* Header with Prominent Shopping Cart */}
                    <div className="flex items-center justify-between gap-2 pb-3 mb-3 border-b border-pink-100">
                      <span className="text-xs sm:text-sm md:text-base font-extrabold text-slate-900 flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#E60067] shadow-xs animate-pulse" />
                        หมวดหมู่สินค้ากิฟฟารีน
                      </span>

                      {/* Prominent Shopping Cart Button */}
                      <button
                        type="button"
                        onClick={onOpenCart}
                        className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-2xl bg-gradient-to-r from-pink-500 to-[#E60067] hover:opacity-95 text-white font-extrabold text-xs sm:text-sm shadow-md transition-all active:scale-95 cursor-pointer ring-2 ring-pink-500/20"
                        title={`เปิดตะกร้าสินค้า (${effectiveCartCount} ชิ้น)`}
                      >
                        <div className="relative flex items-center justify-center">
                          <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                          {effectiveCartCount > 0 && (
                            <span className="absolute -top-2 -right-2.5 bg-white text-[#E60067] text-[9px] font-black min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center shadow-xs border border-pink-100">
                              {effectiveCartCount > 99 ? '99+' : effectiveCartCount}
                            </span>
                          )}
                        </div>
                        <span>ตะกร้าสินค้า</span>
                        {effectiveCartCount > 0 && (
                          <span className="bg-white/20 px-1.5 py-0.5 rounded-md text-[10px] sm:text-xs font-mono">
                            ({effectiveCartCount})
                          </span>
                        )}
                      </button>
                    </div>

                    {/* 2 Rows x 3 Columns (3 เมนูแถวบน, 3 เมนูแถวล่าง) - 50% Larger Spheres & Icons */}
                    <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-5 my-auto py-2">
                      {CHAT_CATEGORIES.map((cat) => {
                        const IconComponent = cat.icon;
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => handleSendMessage(cat.query)}
                            className="flex flex-col items-center gap-2 p-1.5 sm:p-2 rounded-2xl hover:bg-pink-50/80 text-center group cursor-pointer transition-all duration-200 hover:-translate-y-1 active:scale-95"
                            title={cat.name}
                          >
                            {/* Pearl 3D Sphere (+50% Larger: 56-72px) */}
                            <div className="relative w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 lg:w-20 lg:h-20 rounded-full flex items-center justify-center transition-all duration-200 bg-[radial-gradient(ellipse_at_30%_25%,_#FFFFFF_0%,_#F8FAFC_45%,_#CBD5E1_100%)] shadow-lg shadow-slate-300/40 border-2 border-white group-hover:border-pink-300 group-hover:shadow-pink-300/50 group-hover:scale-105">
                              {/* Specular Highlight Top Arc */}
                              <div className="absolute top-1 left-1.5 w-4.5 sm:w-5 h-2 rounded-full bg-white/95 blur-[0.3px] -rotate-20 pointer-events-none" />
                              {/* Bottom Rim Reflection */}
                              <div className="absolute bottom-1 right-1.5 w-3 sm:w-4 h-1.5 rounded-full bg-white/50 blur-[0.4px] pointer-events-none" />
                              <div className={`relative z-10 transition-transform duration-200 group-hover:scale-110 drop-shadow-xs ${cat.iconColor}`}>
                                <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" strokeWidth={1.8} />
                              </div>
                            </div>
                            <span className="text-xs sm:text-sm font-bold text-slate-800 group-hover:text-[#E60067] text-center leading-tight transition-colors line-clamp-2 max-w-[85px] sm:max-w-[105px]">
                              {cat.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Unified Product Details & 10 Benefits (If Opened) */}
                {isProductDetailUnified && (
                  <div className="pt-4 border-t border-pink-100">
                    <div className="bg-gradient-to-b from-slate-50/50 to-white border-2 border-pink-200 rounded-3xl p-5 sm:p-6 shadow-md max-w-2xl mx-auto w-full space-y-4">
                      <div className="flex items-center justify-between border-b border-pink-100 pb-3">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-[#E60067]" />
                          <span className="font-extrabold text-sm sm:text-base text-[#E60067] tracking-wider uppercase">
                            รายละเอียดสินค้า & คุณประโยชน์เด่น 10 ข้อ
                          </span>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-pink-50 text-[#E60067] border border-pink-100">
                          รหัส {latestBotMsg.productDetail.code || latestBotMsg.productDetail.id}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                        <div className="bg-white p-4 rounded-2xl border border-slate-100 flex items-center justify-center shadow-2xs aspect-square max-w-[220px] mx-auto w-full">
                          <img
                            src={latestBotMsg.productDetail.image || 'https://www.giffarine.com/image/goods/82023-N1_01.jpg'}
                            alt={latestBotMsg.productDetail.name}
                            referrerPolicy="no-referrer"
                            className="w-40 h-40 object-contain rounded-xl"
                          />
                        </div>
                        <div className="space-y-3">
                          <h4 className="font-extrabold text-base sm:text-lg text-slate-900 leading-snug">
                            {latestBotMsg.productDetail.name}
                          </h4>
                          <div className="flex items-baseline gap-2">
                            <span className="text-[#E60067] font-black text-xl">
                              ฿{(latestBotMsg.productDetail.memberPrice || latestBotMsg.productDetail.price || 0).toLocaleString()}
                            </span>
                            {latestBotMsg.productDetail.price && latestBotMsg.productDetail.price > (latestBotMsg.productDetail.memberPrice || 0) && (
                              <span className="text-xs text-slate-400 line-through">
                                (ปกติ ฿{latestBotMsg.productDetail.price.toLocaleString()})
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-emerald-600 font-extrabold bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-100 inline-block">
                            +{latestBotMsg.productDetail.pv || Math.round(Number(latestBotMsg.productDetail.memberPrice || latestBotMsg.productDetail.price || 0) * 0.75)} PV
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAddProductToCart(latestBotMsg.productDetail)}
                            className="w-full py-3 rounded-2xl bg-gradient-to-r from-[#E60067] to-[#D80056] text-white font-extrabold text-sm flex items-center justify-center gap-2 shadow-md cursor-pointer active:scale-95"
                          >
                            <ShoppingBag className="w-4 h-4" />
                            <span>ใส่ตะกร้า</span>
                          </button>
                        </div>
                      </div>

                      {/* 10 Benefits list */}
                      <div className="space-y-2.5 pt-3 border-t border-dashed border-pink-100">
                        <p className="font-extrabold text-xs sm:text-sm text-slate-800 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#E60067] animate-pulse" />
                          10 คุณประโยชน์เด่นและข้อดีสำคัญ:
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                          {generate10Benefits(latestBotMsg.productDetail).map((benefit, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-700 bg-white/70 p-2 rounded-xl border border-pink-50">
                              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-pink-50 text-[#E60067] text-[10px] font-black shrink-0 mt-0.5 border border-pink-100">
                                {i + 1}
                              </span>
                              <span className="leading-snug font-medium text-slate-700">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Order Status Tracking Card */}
                {hasOrderStatus && latestBotMsg?.orderStatusResult && (
                  <div className="pt-3 border-t border-pink-100">
                    <div className="bg-teal-50/80 p-4 sm:p-5 rounded-2xl border border-teal-200 shadow-xs space-y-3">
                      <div className="flex items-center justify-between border-b border-teal-100 pb-2">
                        <div className="flex items-center gap-2">
                          <Truck className="w-5 h-5 text-teal-600" />
                          <span className="font-bold text-sm text-slate-900">
                            สถานะคำสั่งซื้อ #{latestBotMsg.orderStatusResult.orderId}
                          </span>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-teal-100 text-teal-800 border border-teal-200">
                          {latestBotMsg.orderStatusResult.status}
                        </span>
                      </div>

                      <div className="text-xs sm:text-sm space-y-1.5 text-slate-700">
                        <div>ลูกค้า: <strong className="text-slate-900">{latestBotMsg.orderStatusResult.customerName}</strong></div>
                        <div>ยอดรวม: <strong className="text-[#E60067]">฿{latestBotMsg.orderStatusResult.totalAmount?.toLocaleString()}</strong></div>
                        {latestBotMsg.orderStatusResult.trackingNumber && (
                          <div className="flex items-center gap-2 pt-1">
                            <span>เลขแทรคกิ้ง:</span>
                            <span className="font-mono font-bold text-teal-800 bg-white px-2.5 py-1 rounded-lg border border-teal-200">
                              {latestBotMsg.orderStatusResult.trackingNumber}
                            </span>
                            <button
                              onClick={() => copyToClipboard(latestBotMsg.orderStatusResult.trackingNumber)}
                              className="p-1.5 text-slate-400 hover:text-teal-600 transition-colors cursor-pointer"
                              title="คัดลอกเลขแทรคกิ้ง"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            {copiedTracking && <span className="text-xs text-emerald-600 font-bold">คัดลอกแล้ว!</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Rich Suggested Products Cards with Search Control beside "สินค้าที่เกี่ยวข้อง:" */}
                {hasSuggestedProducts && (
                  <div className="pt-4 border-t border-pink-100">
                    <SuggestedProductsSection
                      products={latestBotMsg.suggestedProducts!}
                      addedItemIds={addedItemIds}
                      onAddToCart={handleAddProductToCart}
                      onViewDetails={handleShowProductDetails}
                      onSearch={(term) => handleSendMessage(term)}
                    />
                  </div>
                )}

              </div>
            </div>
          );
        })()}

        {/* Bottom spacing and anchor */}
        <div ref={messagesBottomRef} className="h-4" />
      </main>

      {/* Sticky Bottom Message Input Bar */}
      <div className="sticky bottom-0 z-30 bg-white/95 backdrop-blur-md border-t border-pink-100 shadow-[0_-4px_20px_rgba(230,0,103,0.06)] py-3 px-4 sm:px-6">
        <div className="max-w-4xl lg:max-w-5xl xl:max-w-6xl w-full mx-auto">
          <div className="flex items-center gap-3 sm:gap-4 md:gap-5">
            {/* Spacer matching Bot Avatar to align input bar width exactly with message bubble */}
            <div className="w-12 sm:w-14 md:w-16 shrink-0 opacity-0 pointer-events-none" />

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="flex-1 min-w-0 flex items-center gap-2 sm:gap-3"
            >
              <div className="relative flex-1">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    resetInactivityTimer();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="พิมพ์สอบถามสินค้า แนะนำของขวัญ หรือเช็กพัสดุกับพิงค์กิฟ..."
                  disabled={loading}
                  className="w-full bg-pink-50/40 border border-pink-200 focus:bg-white rounded-2xl px-4 py-3 text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#E60067] focus:ring-2 focus:ring-pink-500/15 transition-all shadow-2xs"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !inputValue.trim()}
                className="p-3 sm:px-5 sm:py-3 rounded-2xl bg-gradient-to-r from-[#E60067] to-pink-500 hover:opacity-95 text-white font-bold text-xs sm:text-sm disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-pink-500/25 transition-all cursor-pointer flex items-center justify-center gap-2 shrink-0 active:scale-95"
                title="ส่งข้อความ"
              >
                <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">ส่ง</span>
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* FEEDBACK & RATING MODAL (1-5 STARS) */}
      {isFeedbackModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-pink-100 space-y-5 animate-scale-up">
            {/* Modal Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-[#E60067] to-pink-400 flex items-center justify-center text-white shadow-md shadow-pink-500/20">
                  <Star className="w-5 h-5 fill-amber-300 text-amber-300" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900 tracking-tight">
                    ประเมินความพึงพอใจ Chat & Shop
                  </h3>
                  <p className="text-xs text-slate-500 font-mono">
                    Ticket #{ticketId}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsFeedbackModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* If Inactivity Timeout notice */}
            {isSessionClosed && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-2xl text-xs flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                <span>การสนทนาปิดลงอัตโนมัติเนื่องจากไม่มีการตอบกลับเกิน 3 นาทีค่ะ</span>
              </div>
            )}

            {/* Submitted State Confirmation */}
            {feedbackSubmitted ? (
              <div className="text-center py-6 space-y-3">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="text-sm font-black text-slate-900">
                  ขอบคุณสำหรับคะแนนและข้อเสนอแนะค่ะ!
                </h4>
                <p className="text-xs text-slate-600 leading-relaxed max-w-xs mx-auto">
                  พิงค์กิฟและทีมงานจะนำข้อเสนอแนะไปพัฒนาความฉลาดและการให้บริการของ AI ให้ดียิ่งขึ้นนะคะ ❤️
                </p>

                <div className="pt-3 flex items-center justify-center gap-2">
                  <button
                    onClick={handleStartNewChat}
                    className="px-4 py-2 bg-[#E60067] hover:bg-pink-700 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-pink-500/20 flex items-center gap-1.5"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>เริ่มการสนทนาใหม่</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsFeedbackModalOpen(false);
                      onBackToStore();
                    }}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all"
                  >
                    กลับสู่หน้าร้าน
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-xs text-slate-600 text-center leading-relaxed">
                  คุณลูกค้าพึงพอใจการให้บริการและคำแนะนำของ <strong>PINKGIFF AI</strong> ในระดับใดคะ?
                </p>

                {/* 1-5 Interactive Gold Stars */}
                <div className="flex flex-col items-center gap-2 py-2">
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const active = star <= (hoverRating || feedbackRating);
                      return (
                        <button
                          key={star}
                          type="button"
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => setFeedbackRating(star)}
                          className="p-1 text-slate-200 hover:scale-115 transition-transform cursor-pointer"
                        >
                          <Star
                            className={`w-8 h-8 ${
                              active
                                ? 'text-amber-400 fill-amber-400 drop-shadow-sm'
                                : 'text-slate-200'
                            }`}
                          />
                        </button>
                      );
                    })}
                  </div>

                  {/* Rating Description Label */}
                  <div className="text-xs font-bold text-amber-700 font-sans">
                    {(hoverRating || feedbackRating) === 5 && '🌟 ยอดเยี่ยมมาก ประทับใจที่สุด'}
                    {(hoverRating || feedbackRating) === 4 && '😊 ดีมาก ให้คำแนะนำชัดเจน'}
                    {(hoverRating || feedbackRating) === 3 && '😐 ปานกลาง ตอบคำถามได้'}
                    {(hoverRating || feedbackRating) === 2 && '😕 พอใช้ ต้องปรับปรุงบางจุด'}
                    {(hoverRating || feedbackRating) === 1 && '⚠️ ควรปรับปรุงคำตอบและน้ำเสียง'}
                  </div>
                </div>

                {/* Optional Feedback Comment */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    ข้อเสนอแนะเพิ่มเติมเพื่อพัฒนา AI (ไม่บังคับ):
                  </label>
                  <textarea
                    rows={3}
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="เช่น อยากให้ตอบเร็วขึ้น, อธิบายโปรโมชั่นแต้ม PV ให้ละเอียดขึ้น..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#E60067] focus:ring-2 focus:ring-pink-500/10 transition-all"
                  />
                </div>

                {/* Submit Feedback Button */}
                <div className="pt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFeedbackModalOpen(false)}
                    className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold transition-all"
                  >
                    ข้าม
                  </button>
                  <button
                    type="button"
                    disabled={submittingFeedback}
                    onClick={() => handleSubmitFeedback()}
                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#E60067] to-pink-500 hover:opacity-95 text-white text-xs font-bold transition-all shadow-md shadow-pink-500/20 flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {submittingFeedback ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>กำลังส่ง...</span>
                      </>
                    ) : (
                      <>
                        <ThumbsUp className="w-3.5 h-3.5" />
                        <span>ส่งคะแนนประเมิน</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Zero-Trust Client-Side PIN Verification Modal Overlay */}
      <PinVerificationModal
        isOpen={isPinModalOpen}
        onClose={() => setIsPinModalOpen(false)}
        user={customerUser}
        expectedPin={customerUser?.pinHash}
        onSuccess={handlePinSuccess}
      />
    </div>
  );
};
